import { Alert, AlertTitle, Box, Button, IconButton, SvgIcon, Typography } from '@mui/material';
import type { Router } from 'found';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import CloseIcon from '../../assets/close.svg';
import { palette, spaces } from '../../assets/styles/themes/tokens';
import { externalLinks, routes } from '../../constants';
import type { User } from '../../types/User';
import getMuiIcon from '../../utils/getMuiIcon';
import useRouter from '../../utils/useRouter';
import {
	getUserUpgradeCategory,
	UserUpgradeCategory,
} from '../ReaderBannerContainer/utils/upgradeBannerUtils';

interface UpgradeOfferAlertProps {
	user: User;
}

const getAlertPropsForScenario = (userUpgradeCategory: UserUpgradeCategory, router: Router) => {
	const title = 'Your subscription has limited video access.';
	switch (userUpgradeCategory) {
		case UserUpgradeCategory.LIBRARY_USER:
			return {
				title,
				message:
					"Learn more about how to unlock all the platform's features to make your lesson preparation " +
					'more effective and stress-free.',
				action: () => window.open(externalLinks.schoolLicenceOffer, '_blank'),
			};
		case UserUpgradeCategory.UPGRADE_ALLOWED:
			return {
				title,
				message:
					'Customize your subscription to unlock all the features of the platform and make your lesson ' +
					'preparation more effective and stress-free.',
				action: () => router.push(`/${routes.upgradePackageSelection.root}`),
			};
		default:
			return { title };
	}
};

const UpgradeOfferAlert: React.FC<UpgradeOfferAlertProps> = ({ user }) => {
	const { t } = useTranslation();
	const { router } = useRouter();
	const userUpgradeCategory = getUserUpgradeCategory(user);
	const { title, message, action } = getAlertPropsForScenario(userUpgradeCategory, router);
	const [openUpgradeOfferAlert, setOpenUpgradeOfferAlert] = useState(true);

	const actions = (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'row',
				gap: spaces.md,
				alignItems: 'anchor-center',
				paddingLeft: spaces.md,
			}}
		>
			{action && (
				<Button
					variant="contained"
					color="inherit"
					size="small"
					startIcon={
						<SvgIcon component={getMuiIcon('outlineStar')} sx={{ fill: palette.text.primary }} />
					}
					onClick={action}
					sx={{ width: 'max-content', padding: `${spaces.xxs} ${spaces.sm}` }}
				>
					{t('Learn more')}
				</Button>
			)}
			<IconButton
				data-testid="close-upgrade-offer-alert"
				color="inherit"
				onClick={() => setOpenUpgradeOfferAlert(false)}
			>
				<CloseIcon />
			</IconButton>
		</Box>
	);

	if (!openUpgradeOfferAlert) return null;

	return (
		<Alert
			severity="info"
			variant="filled"
			action={actions}
			onClose={() => {}}
			sx={{
				alignItems: !message ? 'center' : 'flex-start',
				zIndex: 1,
				border: 'none',
				'& .MuiAlert-action': {
					paddingLeft: 0,
				},
			}}
		>
			<AlertTitle sx={{ margin: !message ? 0 : undefined }}>{t(title)}</AlertTitle>
			{message && <Typography variant="body2">{t(message)}</Typography>}
		</Alert>
	);
};

export default UpgradeOfferAlert;
